<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import ScheduledMessagesAPI from 'custom/api/scheduledMessages';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import {
  SCHEDULE_EVENTS,
  browserTimeZone,
  fromInputs,
  quickTimes,
  toDateInput,
  toTimeInput,
} from './helpers';

// Schedules a new message in a conversation, or edits an existing schedule (pass `scheduledMessage`)
const props = defineProps({
  conversationId: { type: Number, required: true },
});

const emit = defineEmits(['scheduled']);

const { t, locale } = useLocoI18n();
const dialogRef = ref(null);
const isSaving = ref(false);
const editing = ref(null);
const fromReplyBox = ref(false);

const FREQUENCIES = ['none', 'daily', 'weekly', 'monthly'];
const VARIABLE_EXAMPLE = '{{contact.name}}';

const blankForm = () => {
  const { tomorrow } = quickTimes();
  return {
    content: '',
    private: false,
    date: toDateInput(tomorrow),
    time: toTimeInput(tomorrow),
    frequency: 'none',
    interval: 1,
    weekdays: [tomorrow.getDay()],
    monthDay: tomorrow.getDate(),
    endsType: 'never',
    endsOn: '',
    endsCount: 5,
    cancelOnReply: false,
    skipIfResolved: false,
    files: [],
  };
};

const form = reactive(blankForm());
const timeZone = browserTimeZone();

const weekdayNames = computed(() => {
  const formatter = new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    weekday: 'short',
  });
  // 2026-10-18 is a Sunday
  return [0, 1, 2, 3, 4, 5, 6].map(day =>
    formatter.format(new Date(2026, 9, 18 + day))
  );
});

const runAt = computed(() => fromInputs(form.date, form.time));
const isPast = computed(
  () => Number.isNaN(runAt.value.getTime()) || runAt.value <= new Date()
);
const isEmpty = computed(
  () => !form.content.trim() && !form.files.length && !editing.value
);

const recurrence = computed(() => {
  if (form.frequency === 'none') return null;
  return {
    frequency: form.frequency,
    interval: Number(form.interval) || 1,
    weekdays: form.frequency === 'weekly' ? form.weekdays : undefined,
    month_day:
      form.frequency === 'monthly' ? Number(form.monthDay) || 1 : undefined,
    ends: {
      type: form.endsType,
      on: form.endsType === 'on' ? form.endsOn : undefined,
      count: form.endsType === 'after' ? Number(form.endsCount) : undefined,
    },
  };
});

const pick = date => {
  form.date = toDateInput(date);
  form.time = toTimeInput(date);
};

const toggleWeekday = day => {
  form.weekdays = form.weekdays.includes(day)
    ? form.weekdays.filter(item => item !== day)
    : [...form.weekdays, day].sort();
};

const onFiles = event => {
  form.files = Array.from(event.target.files || []);
};

// open({ content, private }) from the reply box, or open({ scheduledMessage }) to edit
const open = ({ content = '', isPrivate = false, scheduledMessage } = {}) => {
  Object.assign(form, blankForm());
  editing.value = scheduledMessage || null;
  fromReplyBox.value = !scheduledMessage && Boolean(content);

  if (scheduledMessage) {
    const when = new Date(scheduledMessage.next_run_at * 1000);
    const rule = scheduledMessage.recurrence || {};
    Object.assign(form, {
      content: scheduledMessage.content || '',
      private: scheduledMessage.private,
      date: toDateInput(when),
      time: toTimeInput(when),
      frequency: rule.frequency || 'none',
      interval: rule.interval || 1,
      weekdays: rule.weekdays || [when.getDay()],
      monthDay: rule.month_day || when.getDate(),
      endsType: rule.ends?.type || 'never',
      endsOn: rule.ends?.on || '',
      endsCount: rule.ends?.count || 5,
      cancelOnReply: scheduledMessage.cancel_on_reply,
      skipIfResolved: scheduledMessage.skip_if_resolved,
    });
  } else {
    form.content = content;
    form.private = isPrivate;
  }
  dialogRef.value.open();
};

const submit = async () => {
  if (isPast.value) {
    useAlert(t('LOCO.SCHEDULED.ERROR_PAST'));
    return;
  }
  if (isEmpty.value) {
    useAlert(t('LOCO.SCHEDULED.ERROR_EMPTY'));
    return;
  }

  const payload = {
    content: form.content,
    private: form.private,
    next_run_at: runAt.value.toISOString(),
    time_zone: timeZone,
    recurrence: recurrence.value,
    cancel_on_reply: form.cancelOnReply,
    skip_if_resolved: form.skipIfResolved,
  };

  isSaving.value = true;
  try {
    if (editing.value) {
      await ScheduledMessagesAPI.update(editing.value.id, payload);
      useAlert(t('LOCO.SCHEDULED.SAVED'));
    } else {
      await ScheduledMessagesAPI.createInConversation(props.conversationId, {
        ...payload,
        files: form.files,
      });
      useAlert(t('LOCO.SCHEDULED.SCHEDULED'));
      if (fromReplyBox.value) emitter.emit(SCHEDULE_EVENTS.REPLY_SCHEDULED);
    }
    emitter.emit(SCHEDULE_EVENTS.CHANGED, props.conversationId);
    emit('scheduled');
    dialogRef.value.close();
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message;
    useAlert(t('LOCO.SCHEDULED.ERROR', { error: message }));
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="
      editing ? t('LOCO.SCHEDULED.EDIT_TITLE') : t('LOCO.SCHEDULED.TITLE')
    "
    :confirm-button-label="
      editing ? t('LOCO.SCHEDULED.SAVE') : t('LOCO.SCHEDULED.SUBMIT')
    "
    :cancel-button-label="t('LOCO.SCHEDULED.CANCEL')"
    :is-loading="isSaving"
    @confirm="submit"
  >
    <div class="flex flex-col gap-5 text-sm text-n-slate-12">
      <label class="flex flex-col gap-1">
        <span class="font-medium">{{ t('LOCO.SCHEDULED.CONTENT') }}</span>
        <textarea
          v-model="form.content"
          rows="4"
          class="!mb-0 resize-y"
          :placeholder="t('LOCO.SCHEDULED.CONTENT_PLACEHOLDER')"
        />
        <span class="text-xs text-n-slate-11">
          {{
            t('LOCO.SCHEDULED.VARIABLES_HINT', { example: VARIABLE_EXAMPLE })
          }}
        </span>
      </label>

      <label class="flex items-center gap-2">
        <input v-model="form.private" type="checkbox" />
        <span>{{ t('LOCO.SCHEDULED.PRIVATE') }}</span>
      </label>

      <div class="flex flex-col gap-2">
        <span class="font-medium">{{ t('LOCO.SCHEDULED.WHEN') }}</span>
        <div class="flex flex-wrap gap-2">
          <Button
            xs
            faded
            slate
            type="button"
            :label="t('LOCO.SCHEDULED.IN_AN_HOUR')"
            @click="pick(quickTimes().inAnHour)"
          />
          <Button
            xs
            faded
            slate
            type="button"
            :label="t('LOCO.SCHEDULED.TOMORROW')"
            @click="pick(quickTimes().tomorrow)"
          />
          <Button
            xs
            faded
            slate
            type="button"
            :label="t('LOCO.SCHEDULED.MONDAY')"
            @click="pick(quickTimes().monday)"
          />
        </div>
        <div class="flex gap-3">
          <label class="flex flex-col gap-1 flex-1">
            <span class="text-xs text-n-slate-11">{{
              t('LOCO.SCHEDULED.DATE')
            }}</span>
            <input v-model="form.date" type="date" class="!mb-0" />
          </label>
          <label class="flex flex-col gap-1 flex-1">
            <span class="text-xs text-n-slate-11">{{
              t('LOCO.SCHEDULED.TIME')
            }}</span>
            <input v-model="form.time" type="time" class="!mb-0" />
          </label>
        </div>
        <span class="text-xs text-n-slate-11">
          {{ t('LOCO.SCHEDULED.TIME_ZONE', { zone: timeZone }) }}
        </span>
        <span v-if="isPast" class="text-xs text-n-ruby-11">
          {{ t('LOCO.SCHEDULED.ERROR_PAST') }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <span class="font-medium">{{ t('LOCO.SCHEDULED.REPEAT') }}</span>
        <select v-model="form.frequency" class="!mb-0">
          <option
            v-for="frequency in FREQUENCIES"
            :key="frequency"
            :value="frequency"
          >
            {{ t(`LOCO.SCHEDULED.REPEAT_${frequency.toUpperCase()}`) }}
          </option>
        </select>

        <template v-if="form.frequency !== 'none'">
          <div class="flex items-center gap-2">
            <span>{{ t('LOCO.SCHEDULED.EVERY') }}</span>
            <input
              v-model="form.interval"
              type="number"
              min="1"
              max="365"
              class="!mb-0 !w-20"
            />
            <span>{{ t(`LOCO.SCHEDULED.EVERY_UNIT.${form.frequency}`) }}</span>
          </div>

          <div
            v-if="form.frequency === 'weekly'"
            class="flex flex-wrap items-center gap-1"
          >
            <span class="me-1">{{ t('LOCO.SCHEDULED.ON_DAYS') }}</span>
            <button
              v-for="(name, day) in weekdayNames"
              :key="day"
              type="button"
              class="h-7 px-2 rounded-md text-xs outline outline-1 -outline-offset-1"
              :class="
                form.weekdays.includes(day)
                  ? 'bg-n-brand/10 outline-n-brand text-n-blue-11'
                  : 'outline-n-weak text-n-slate-11'
              "
              @click="toggleWeekday(day)"
            >
              {{ name }}
            </button>
          </div>

          <label
            v-if="form.frequency === 'monthly'"
            class="flex items-center gap-2"
          >
            <span>{{ t('LOCO.SCHEDULED.DAY_OF_MONTH') }}</span>
            <input
              v-model="form.monthDay"
              type="number"
              min="1"
              max="31"
              class="!mb-0 !w-20"
            />
          </label>

          <div class="flex flex-wrap items-center gap-2">
            <span>{{ t('LOCO.SCHEDULED.ENDS') }}</span>
            <select v-model="form.endsType" class="!mb-0 !w-auto">
              <option value="never">
                {{ t('LOCO.SCHEDULED.ENDS_NEVER') }}
              </option>
              <option value="on">{{ t('LOCO.SCHEDULED.ENDS_ON') }}</option>
              <option value="after">
                {{ t('LOCO.SCHEDULED.ENDS_AFTER') }}
              </option>
            </select>
            <input
              v-if="form.endsType === 'on'"
              v-model="form.endsOn"
              type="date"
              class="!mb-0 !w-auto"
            />
            <input
              v-if="form.endsType === 'after'"
              v-model="form.endsCount"
              type="number"
              min="1"
              max="1000"
              class="!mb-0 !w-20"
              :aria-label="t('LOCO.SCHEDULED.ENDS_COUNT')"
            />
          </div>
        </template>
      </div>

      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2">
          <input v-model="form.cancelOnReply" type="checkbox" />
          <span>{{ t('LOCO.SCHEDULED.CANCEL_ON_REPLY') }}</span>
        </label>
        <label class="flex items-center gap-2">
          <input v-model="form.skipIfResolved" type="checkbox" />
          <span>{{ t('LOCO.SCHEDULED.SKIP_IF_RESOLVED') }}</span>
        </label>
      </div>

      <label v-if="!editing" class="flex flex-col gap-1">
        <span class="font-medium">{{ t('LOCO.SCHEDULED.FILES') }}</span>
        <input type="file" multiple class="!mb-0" @change="onFiles" />
      </label>
    </div>
  </Dialog>
</template>
